import type { PurchaseSource } from '@sunflower-games/analytics-events';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import compact from 'lodash/compact';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { action, autorun, makeObservable, observable } from 'mobx';

import type { StoreOfferType } from '../../components/StoreOffers/types';
import {
  SOCKET_CLAIM_PACKAGE_COMPLETED,
  SOCKET_EVENT_STARTED,
  SOCKET_USER_EVENT_TRIGGER,
} from '../../config/constants';
import { analytics } from '../../services/analytics';
import type { EventAction, EventFeatureSubType, WidgetEventOptionalArgs } from '../../services/analytics/types';
import { postRequest } from '../../services/api';
import { logToNewRelic } from '../../services/debug';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import {
  isActionEventTrigger,
  isCoinBackBoosterEventTrigger,
  isGiftPopupType,
  isOfferPopupType,
  isOfferV2PopupType,
  isPersonalGiftPopupType,
  isQuestEventTrigger,
  isRaffleBenefit,
  isRollingOfferPopupType,
  isWithinRewardClaimWindow,
} from '../../utils/events';
import { sleep } from '../../utils/helpers';
import { devError, devLog } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { formatTime } from '../../utils/time';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import { type RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';
import { EnumOpenDialog } from '../ui/types';

import { POPUP_ANIMATIOM_DURATION_SECS, RAFFLE_STAGE_TO_EVENT_NAME, WIDGET_SAFE_ID_PREFIX } from './constants';
import { reduceInvalidEventsData, reduceInvalidPersonalGifts } from './eventTriggerSchemaValidation';
import {
  type Benefit,
  EVENT_TRIGGER_ACTION_TYPE,
  type EventTriggerPopupData,
  type EventTriggerPopupType,
  type EventTriggersResponse,
  type OfferPackage,
  type OfferV2PopupType,
  type RoadUserState,
  type RollingOfferPopupType,
} from './types';

type StoreCompatibleBenefit = OfferV2PopupType | RollingOfferPopupType;

export class EventTriggerStore extends Loadable implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  // Track events that have already been shown to prevent re-triggering
  private shownEventIds = new Set<string>();

  private cleanupTimers() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
    if (this.activeBenefitPackagesTimerInterval) {
      clearInterval(this.activeBenefitPackagesTimerInterval);
      this.activeBenefitPackagesTimerInterval = undefined;
    }
  }

  @action
  public reset() {
    this.widgetEventsLists = [];
    this.activeEventsPopupsQueue = [];
    this.currentActiveEventPopup = null;
    this.lastEventTriggerResponse = null;
    this.storeOffers = [];
    this.timers = {};
    this.activeBenefitPackagesTimers = {};
    this.shownEventIds.clear();
    this.isPopupOpen = false;
    this.isPopupCloseAnimationRunning = false;
    this.isBusy = false;
    this.isActionBusy = false;
    this.isDailyBonusClosed = false;
    this.fetchCount = 0;
    this.onCloseListener = undefined;
    this.setLoading(false);
    this.cleanupTimers();
  }

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);
    const subscription = this.subscribeToEventsReceivedSocket.bind(this);
    const claimPackageSubscription = this.subscribeToClaimPackage.bind(this);
    const eventStartedSubscription = this.subscribeToEventStartedSocket.bind(this);

    this.initializeFeatureFlags();
    this.initIframePostMessage();

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        socket.on(SOCKET_USER_EVENT_TRIGGER, subscription);
        socket.on(SOCKET_CLAIM_PACKAGE_COMPLETED, claimPackageSubscription);
        socket.on(SOCKET_EVENT_STARTED, eventStartedSubscription);
      } else {
        socket.off(SOCKET_USER_EVENT_TRIGGER, subscription);
        socket.off(SOCKET_CLAIM_PACKAGE_COMPLETED, claimPackageSubscription);
        socket.off(SOCKET_EVENT_STARTED, eventStartedSubscription);
        // Clear user-scoped state so the next session doesn't inherit it
        this.reset();
      }
    });
  }

  private initializeFeatureFlags(): void {
    this.offerAnimationFlag = resolveFlag('enable_offer_rive_animation', true);
    this.giftAnimationFlag = resolveFlag('enable_gift_rive_animation', true);
    this.messageAnimationFlag = resolveFlag('enable_message_rive_animation', true);

    ldClient?.on('ready', () => {
      if (!ldClient) return;

      const enableOfferRiveAnimation = !!ldClient.variation('enable_offer_rive_animation', true);
      this.updateOfferAnimationFlag(enableOfferRiveAnimation);

      const enableGiftRiveAnimation = !!ldClient.variation('enable_gift_rive_animation', true);
      this.updateGiftAnimationFlag(enableGiftRiveAnimation);

      const enableMessageRiveAnimation = !!ldClient.variation('enable_message_rive_animation', true);
      this.updateMessageAnimationFlag(enableMessageRiveAnimation);
    });

    ldClient?.on('change', (changes) => {
      if ('enable_offer_rive_animation' in changes) {
        const newValue = changes.enable_offer_rive_animation.current;
        this.updateOfferAnimationFlag(newValue);
      }

      if ('enable_gift_rive_animation' in changes) {
        const newValue = changes.enable_gift_rive_animation.current;
        this.updateGiftAnimationFlag(newValue);
      }

      if ('enable_message_rive_animation' in changes) {
        const newValue = changes.enable_message_rive_animation.current;
        this.updateMessageAnimationFlag(newValue);
      }
    });
  }

  public dialogTypes: FeatureManagerDialogType[] = [
    'EventTriggerGifts',
    'PersonalGift',
    'EventTriggerMessages',
    'EventTriggerOffers',
    'EventTriggerOffersV2',
    'EventTriggerRollingOffers',
    'EventTriggerRoad',
  ];

  public lastOfferSource: PurchaseSource = 'offer';
  public roadSource: PurchaseSource = 'road';

  public async startDialogs() {
    return this.showNextPopup();
  }

  private fetchCount = 0;
  private incrementFetchCount() {
    this.fetchCount++;
  }

  private initIframePostMessage() {
    window.addEventListener('message', (e) => {
      const message = e.data;
      const eventTriggerId = message?.payload?.eventTriggerId;
      // Open offer event from CrownSlot
      if (message?.type === 'open_monetization_event' && eventTriggerId) {
        void this.openPopupByEventTriggerId(eventTriggerId);
      }
    });
  }

  public notifyMonetizationClosed() {
    this.rootStore.gameStore.sendSlotsGameUiMessage({ type: 'close_monetization_event' });
  }

  private handleCoinBackBoosterEventTrigger({
    currentEventsByTrigger,
    otherActiveEvents,
  }: {
    currentEventsByTrigger: EventTriggerPopupData[];
    otherActiveEvents: EventTriggerPopupData[];
  }) {
    let newCurrentEventsByTrigger = [...currentEventsByTrigger];
    let newOtherActiveEvents = [...otherActiveEvents];

    const coinBackInCurrentEvents = currentEventsByTrigger.find(
      (event) => event.eventTrigger.type === 'coinBackBooster',
    );
    const coinBackBoosterInOther = otherActiveEvents.find((event) => event.eventTrigger.type === 'coinBackBooster');

    if (coinBackInCurrentEvents && isCoinBackBoosterEventTrigger(coinBackInCurrentEvents.benefit)) {
      this.rootStore.coinBackStore.updateCoinBackBooster(coinBackInCurrentEvents, true);
      newCurrentEventsByTrigger = currentEventsByTrigger.filter(
        (event) => event.eventTrigger.type !== 'coinBackBooster',
      );
    }

    if (coinBackBoosterInOther && isCoinBackBoosterEventTrigger(coinBackBoosterInOther.benefit)) {
      this.rootStore.coinBackStore.updateCoinBackBooster(coinBackBoosterInOther, false);
      newOtherActiveEvents = otherActiveEvents.filter((event) => event.eventTrigger.type !== 'coinBackBooster');
    }

    return { currentEventsByTrigger: newCurrentEventsByTrigger, otherActiveEvents: newOtherActiveEvents };
  }

  private handleActiveEventsPopups(data: EventTriggersResponse) {
    const dataAfterCoinBackBooster = this.handleCoinBackBoosterEventTrigger(data);

    const { currentEventsByTrigger, otherActiveEvents } = dataAfterCoinBackBooster;

    return { ...data, currentEventsByTrigger, otherActiveEvents };
  }

  private compactEventTriggerData(data: EventTriggersResponse): EventTriggersResponse {
    return {
      ...data,
      currentEventsByTrigger: compact(data.currentEventsByTrigger),
      otherActiveEvents: compact(data.otherActiveEvents),
      personalGifts: compact(data.personalGifts),
    };
  }

  async triggerEvent({ triggerKey, additionalParams }: { triggerKey: string; additionalParams?: Record<string, any> }) {
    const csEventStart = resolveFlag('client_side_event_start', true);

    const response = await postRequest<ApiResponse<EventTriggersResponse>>('/v2/event-trigger/events', {
      triggerKey,
      csEventStart,
      ...additionalParams,
    });

    const data = get(response, 'data.data', null);

    if (!isEmpty(data)) {
      const dataWithEventsCompacted = this.compactEventTriggerData(data);

      const activeEventsData = this.handleActiveEventsPopups(dataWithEventsCompacted);
      this.setActiveEventPopups(activeEventsData);

      void analytics.sendEvent('events_fetched', {
        keys: data.currentEventsByTrigger.map((event) => event.benefit?.key ?? event.benefitKey ?? 'unknown').join(','),
      });
      return data;
    }

    this.setLastEventTriggerResponse(null);
    return null;
  }

  async fetchWithPromoCode(promoCode: string) {
    const responseData = await this.triggerEvent({ triggerKey: 'AFTER_PROMO_CODE', additionalParams: { promoCode } });

    if (responseData) {
      // Ensure newly fetched events are displayed immediately, similar to socket flow
      // by adding the relevant dialog types to the sequence queue
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(this.dialogTypes, true);
    }
    return responseData;
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    if (this.rootStore.authStore.userCoolDown) {
      return 'OFF';
    }

    const featureFlag = resolveFlag('liveops', true);
    if (!featureFlag) {
      return 'OFF';
    }

    try {
      const responseData = await this.triggerEvent({ triggerKey: 'AFTER_LOGIN' });
      if (!responseData) {
        return 'ERROR';
      }
      this.incrementFetchCount();
      return 'READY';
    } catch (e) {
      devLog('error fetch event triggers data');
      return 'ERROR';
    }
  });

  @observable
  public offerAnimationFlag: boolean = false;

  @observable
  public giftAnimationFlag: boolean = false;

  @observable
  public messageAnimationFlag: boolean = false;

  private timerInterval: NodeJS.Timeout | undefined = undefined;
  private activeBenefitPackagesTimerInterval: NodeJS.Timeout | undefined = undefined;

  @observable
  public currentActiveEventPopup: EventTriggerPopupData | null = null;

  @observable
  public isBusy = false;

  @observable
  public isPopupOpen = false;

  @observable
  public isPopupCloseAnimationRunning = false;

  @observable
  public activeEventsPopupsQueue: EventTriggerPopupData[] = [];

  @observable
  public widgetEventsLists: EventTriggerPopupData[] = [];

  @observable
  public timers: Record<string, number> = {};

  @observable
  public activeBenefitPackagesTimers: Record<string, number> = {};

  @observable
  public isDailyBonusClosed = false;

  @observable
  public isActionBusy = false;

  @observable
  public lastEventTriggerResponse: EventTriggersResponse | null = null;

  @action
  updateOfferAnimationFlag(flag: boolean) {
    this.offerAnimationFlag = flag;
  }

  @action
  updateGiftAnimationFlag(flag: boolean) {
    this.giftAnimationFlag = flag;
  }

  @action
  updateMessageAnimationFlag(flag: boolean) {
    this.messageAnimationFlag = flag;
  }

  @action
  public setLastEventTriggerResponse(response: EventTriggersResponse | null) {
    this.lastEventTriggerResponse = response;
  }

  public getEventDataByUserEventTriggerId(userEventTriggerId: string) {
    return [
      ...(this.lastEventTriggerResponse?.currentEventsByTrigger || []),
      ...(this.lastEventTriggerResponse?.otherActiveEvents || []),
    ].find((event) => event.userEventTriggerId === userEventTriggerId);
  }

  private isRollingOfferWithPaidCurrentStep(event: EventTriggerPopupData): boolean {
    if (event.benefit.benefitType !== 'rollingOffer') return false;
    const benefit = event.benefit as Benefit<'rollingOffer'>;
    const currentStep = event.currentStep;
    const bp = benefit.benefitPackages?.find((benPackage) => benPackage.order === currentStep);
    const pkg = bp ? benefit.packages?.find((p) => p.packageId === bp.packageId) : null;
    return !pkg || pkg.price > 0;
  }

  private markEventAsInClaimPeriod(userEventTriggerId: string) {
    const arrays = [
      this.lastEventTriggerResponse?.currentEventsByTrigger,
      this.lastEventTriggerResponse?.otherActiveEvents,
    ];
    for (const arr of arrays) {
      const event = arr?.find((e) => e.userEventTriggerId === userEventTriggerId);
      if (event) {
        event.isInClaimPeriod = true;
        return;
      }
    }
  }

  @observable
  public storeOffers: StoreOfferType[] = [];

  @action
  public setStoreOffers(storeOffers: StoreOfferType[]) {
    this.storeOffers = storeOffers;
  }

  @action
  public updateStoreOffers(activeEventsData: EventTriggerPopupData[]) {
    const storeOffersFeatureFlag = resolveFlag('store_offers_feature_enable', true);
    if (!storeOffersFeatureFlag) {
      return;
    }

    const storeOffers = activeEventsData
      .map((event) => {
        const isOfferV2 = isOfferV2PopupType(event.benefit);
        const isRollingOffer = isRollingOfferPopupType(event.benefit);

        if ((!isOfferV2 && !isRollingOffer) || !event.benefit?.visibility?.store) {
          return null;
        }

        const benefit = event.benefit as StoreCompatibleBenefit;
        const { storeLayout, storeLayoutImageUrl, packages, key } = benefit;
        if (!storeLayout || !storeLayoutImageUrl) {
          return null;
        }

        const {
          canDisplay,
          eventTrigger: { eventTriggerId },
          userEventTriggerId,
        } = event;

        return {
          benefitKey: key,
          storeLayout,
          storeLayoutImageUrl,
          packages,
          userEventTriggerId,
          canDisplay,
          eventTriggerId,
        };
      })
      .filter((offer) => offer !== null) as StoreOfferType[];

    this.setStoreOffers(storeOffers);

    const storeOfferTimers = activeEventsData.reduce<Record<string, number>>((acc, event) => {
      const isOfferV2 = isOfferV2PopupType(event.benefit);
      const isRollingOffer = isRollingOfferPopupType(event.benefit);

      if ((!isOfferV2 && !isRollingOffer) || !event.benefit?.visibility?.store || !event.userEventTriggerId) {
        return acc;
      }

      return {
        ...acc,
        [event.userEventTriggerId]: dayjs(event.expiredAt).diff(dayjs(), 'second'),
      };
    }, {});

    if (!isEmpty(storeOfferTimers)) {
      this.setTimers({
        ...this.timers,
        ...storeOfferTimers,
      });
      this.startTimers();
    }
  }

  @action
  setIsBusy(newIsBusy: boolean) {
    this.isBusy = newIsBusy;
  }

  @action
  setCurrentActiveEvent(event: EventTriggerPopupData | null) {
    this.currentActiveEventPopup = event;
  }

  @action
  public updateCurrentEventRoadState(newRoadState: Partial<RoadUserState>, roadEventTriggerId?: string) {
    let current = this.currentActiveEventPopup;
    if (!current && roadEventTriggerId) {
      //if the current is not active because the user closed the popup, find the event in the queue based on the road event trigger id
      current = this.widgetEventsLists.find((event) => event.userEventTriggerId === roadEventTriggerId) ?? null;
    }
    if (!current || !current.roadState || current.benefit.benefitType !== 'road') {
      return;
    }

    const userEventTriggerId = current.userEventTriggerId;

    // Update the active popup state
    this.setCurrentActiveEvent({
      ...current,
      roadState: {
        ...current.roadState,
        ...newRoadState,
      },
    });

    // Mirror the update into the active events queue so it persists across close/reopen
    if (userEventTriggerId) {
      this.setActiveEventsPopupsQueue(
        this.activeEventsPopupsQueue.map((event) => {
          if (event.userEventTriggerId === userEventTriggerId) {
            const baseRoadState = event.roadState ?? current.roadState;
            const updatedRoadState = {
              ...baseRoadState,
              ...newRoadState,
            } as RoadUserState;
            return {
              ...event,
              roadState: updatedRoadState,
            };
          }
          return event;
        }),
      );

      // Mirror the update into widget events (if present there)
      this.updateWidgetEvents(
        this.widgetEventsLists.map((widget) => {
          if (widget.userEventTriggerId === userEventTriggerId) {
            const baseRoadState = widget.roadState ?? current.roadState;
            const updatedRoadState = {
              ...baseRoadState,
              ...newRoadState,
            } as RoadUserState;
            return {
              ...widget,
              roadState: updatedRoadState,
            };
          }
          return widget;
        }),
      );
    }
  }

  @action
  public setDailyBonusClosed(isClosed: boolean) {
    this.isDailyBonusClosed = isClosed;
  }

  @action
  public setPopupCloseAnimationRunning(isRunning: boolean) {
    this.isPopupCloseAnimationRunning = isRunning;
  }

  @action
  private setActiveEventsPopupsQueue(activeEventsData: EventTriggerPopupData[]) {
    this.activeEventsPopupsQueue = activeEventsData.filter((event) => event.benefit.visibility?.lobby !== false);
  }

  @action
  public setIsActionBusy(value: boolean) {
    this.isActionBusy = value;
  }

  @action
  private setTimers(timers: Record<string, number>) {
    this.timers = timers;
  }

  @action
  private setActiveBenefitPackagesTimers(timers: Record<string, number>) {
    this.activeBenefitPackagesTimers = timers;
  }

  @action
  public setWidgetEventsList(newWidgetEvents: EventTriggerPopupData[]) {
    this.widgetEventsLists = newWidgetEvents;
  }

  @action
  public async openPopupByUserEventTriggerId(userEventTriggerId: string, onClose?: () => void) {
    let popup = this.widgetEventsLists.find((event) => event.userEventTriggerId === userEventTriggerId);
    if (popup) {
      if (!popup.canDisplay) {
        const triggerId = popup.isNewEventType ? popup.userEventTriggerId : popup.eventTrigger.eventTriggerId;

        popup = await this.startEventTimer(triggerId);

        if (!popup) {
          devError('Error while starting event timer', { popup });
          return;
        }
      }

      void this.openPopup(popup, onClose);
    }
  }

  @action
  public async openPopupByEventTriggerId(eventTriggerId: string, onClose?: () => void) {
    let popup = this.widgetEventsLists.find((event) => event.eventTrigger.eventTriggerId === eventTriggerId);
    if (popup) {
      if (!popup.canDisplay) {
        const triggerId = popup.isNewEventType ? popup.userEventTriggerId : popup.eventTrigger.eventTriggerId;

        popup = await this.startEventTimer(triggerId);

        if (!popup) {
          devError('Error while starting event timer', { popup });
          return;
        }
      }

      void this.openPopup(popup, onClose);
    }
  }

  @action
  public removeEvent(userEventTriggerId: string) {
    const updatedActiveEvents = [...this.activeEventsPopupsQueue].filter(
      (event) => event.userEventTriggerId !== userEventTriggerId,
    );
    this.setActiveEventsPopupsQueue(updatedActiveEvents);

    this.setStoreOffers(this.storeOffers.filter((storeOffer) => storeOffer.userEventTriggerId !== userEventTriggerId));

    const event = this.getEventDataByUserEventTriggerId(userEventTriggerId);

    const eventHasLimit = event && event.eventTrigger.usesPerUserLimit !== -1;
    const isMessageEvent = event?.eventTrigger.type === 'message';
    const shouldRemoveWidget = !isMessageEvent || event?.eventTrigger.purchaseCooldown || eventHasLimit;
    if (shouldRemoveWidget) {
      const updatedWidgetEvents = [...this.widgetEventsLists].filter(
        (widgetEvent) => widgetEvent.userEventTriggerId !== userEventTriggerId,
      );
      this.updateWidgetEvents(updatedWidgetEvents);
    }

    if (this.currentActiveEventPopup?.userEventTriggerId === userEventTriggerId) {
      this.setCurrentActiveEvent(null);
    }
  }

  public getEventTimerFormatted(key: string, t?: TFunction) {
    const timerSecs = this.timers[key];
    return timerSecs ? formatTime(timerSecs, 's', t) : '0';
  }

  public getBenefitPackageTimerFormatted(key: string, t?: TFunction) {
    const timerSecs = this.activeBenefitPackagesTimers[key];
    return timerSecs ? formatTime(timerSecs, 's', t) : null;
  }

  private startTimers() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      const timers = { ...this.timers };

      Object.keys(timers).forEach((key) => {
        timers[key] = timers[key] - 1;
        if (timers[key] <= 0) {
          const event = this.getEventDataByUserEventTriggerId(key);
          const canExtendToClaimPeriod =
            event?.rewardClaimEndDate &&
            dayjs().isBefore(dayjs(event.rewardClaimEndDate)) &&
            !this.isRollingOfferWithPaidCurrentStep(event);
          if (canExtendToClaimPeriod) {
            timers[key] = dayjs(event.rewardClaimEndDate).diff(dayjs(), 'second');
            this.markEventAsInClaimPeriod(key);
          } else {
            delete timers[key];
            this.removeEvent(key);
          }
        }
      });

      this.setTimers(timers);
    }, 1000);
  }

  startActivePackagesTimers() {
    if (this.activeBenefitPackagesTimerInterval) {
      clearInterval(this.activeBenefitPackagesTimerInterval);
    }
    this.activeBenefitPackagesTimerInterval = setInterval(() => {
      const timers = { ...this.activeBenefitPackagesTimers };

      Object.keys(timers).forEach((key) => {
        timers[key] = timers[key] - 1;
        if (timers[key] <= 0) {
          delete timers[key];
          this.removeEvent(key);
        }
      });

      this.setActiveBenefitPackagesTimers(timers);
    }, 1000);
  }

  async startStoreOfferTimer(eventTriggerId: string) {
    try {
      const event = this.storeOffers.find((storeOffer) => storeOffer.eventTriggerId === eventTriggerId);

      if (!event || event.canDisplay) {
        return;
      }

      const { userEventTriggerId } = event;

      const response = await postRequest<ApiResponse<{ expiredAt: Date | undefined }>>(
        `event-trigger/${eventTriggerId}/start-timer`,
        {
          userEventTriggerId,
        },
      );

      const { expiredAt } = response.data.data;

      if (expiredAt) {
        this.setStoreOffers(
          this.storeOffers.map((storeOffer) => {
            if (storeOffer.eventTriggerId === eventTriggerId) {
              return {
                ...storeOffer,
                canDisplay: true,
              };
            }
            return storeOffer;
          }),
        );
        this.setTimers({
          ...this.timers,
          [event.userEventTriggerId]: dayjs(expiredAt).diff(dayjs(), 'second'),
        });
      }
    } catch (error) {
      devError('Error while starting store offer timer');
    }
  }

  async startEventTimer(eventTriggerId: string): Promise<EventTriggerPopupData | undefined> {
    try {
      const event: EventTriggerPopupData | undefined =
        this.widgetEventsLists.find((eventItem) => eventItem.userEventTriggerId === eventTriggerId) ||
        this.widgetEventsLists.find((eventItem) => eventItem.eventTrigger.eventTriggerId === eventTriggerId);

      if (!event) {
        // When offer only on store
        return undefined;
      }

      const isNewEventType = event.isNewEventType ?? false;
      const { userEventTriggerId } = event;

      const response = await postRequest<ApiResponse<{ expiredAt: Date | undefined }>>(
        `event-trigger/${event.eventTrigger.eventTriggerId}/start-timer`,
        {
          userEventTriggerId,
        },
      );

      const { expiredAt } = response.data.data;

      if (typeof expiredAt === 'undefined') {
        return undefined;
      }

      const updatedEvent = {
        ...event,
        expiredAt,
        canDisplay: true,
      };

      this.setStoreOffers(
        this.storeOffers.map((storeOffer) => {
          if (storeOffer.eventTriggerId === eventTriggerId) {
            return {
              ...storeOffer,
              canDisplay: true,
            };
          }
          return storeOffer;
        }),
      );

      const newWidgetEvents = this.widgetEventsLists.map((widget) => {
        if (widget.userEventTriggerId === updatedEvent.userEventTriggerId) {
          return updatedEvent;
        }
        return widget;
      });

      this.setWidgetEventsList(newWidgetEvents);

      const isInActiveEvents = this.activeEventsPopupsQueue.some(
        (eventItem) => eventItem.userEventTriggerId === updatedEvent.userEventTriggerId,
      );

      if (isInActiveEvents) {
        const newActiveEventsPopupsQueue = this.activeEventsPopupsQueue.map((activeEvent) => {
          if (!isNewEventType && activeEvent.eventTrigger.eventTriggerId === eventTriggerId) {
            return updatedEvent;
          } else if (isNewEventType && activeEvent.userEventTriggerId === eventTriggerId) {
            return updatedEvent;
          }

          return activeEvent;
        });

        this.setStoreOffers(
          this.storeOffers.map((storeOffer) => {
            if (storeOffer.eventTriggerId === eventTriggerId) {
              return {
                ...storeOffer,
                canDisplay: true,
              };
            }
            return storeOffer;
          }),
        );

        this.setActiveEventsPopupsQueue(newActiveEventsPopupsQueue);
      }

      this.updateWidgetOnStartTimer(updatedEvent.userEventTriggerId, expiredAt);

      return updatedEvent;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('Error while starting event timer', errorMessage);
      logToNewRelic('Error starting event timer', errorMessage, 'error');
      return undefined;
    }
  }

  @action
  private setPopupIsOpen(isOpen: boolean) {
    this.isPopupOpen = isOpen;
  }

  public async claimFreePackage(activePackage: OfferPackage) {
    let isClaimed = false;
    if (this.isActionBusy || !this.currentActiveEventPopup) return;

    this.setIsActionBusy(true);

    try {
      if (!this.currentActiveEventPopup || !isRollingOfferPopupType(this.currentActiveEventPopup.benefit)) {
        return;
      }

      const pkg = this.currentActiveEventPopup?.benefit.packages.find((p) => p.packageId === activePackage.packageId);

      if (!pkg || pkg.price !== 0) {
        devError('Cannot claim package with price', activePackage);
        return;
      }

      const { userEventTriggerId, eventTrigger } = this.currentActiveEventPopup;
      const { eventTriggerId } = eventTrigger;

      const response = await postRequest<ApiResponse<{ success: boolean }>>(
        '/v2/offers/rolling-offers/claim-free-package',
        {
          activeEvent: this.currentActiveEventPopup,
          userEventTriggerId,
          eventTriggerId,
          packageId: activePackage.packageId,
        },
      );

      if (response.data.success) {
        isClaimed = true;
        await this.rootStore.featureManagerStore.fetchAndStartDialogs(this.dialogTypes);
      }
    } catch (error) {
      devError('Cannot claim free package', error);
    }

    this.setIsActionBusy(false);
    return isClaimed;
  }

  public getNextPopup(): EventTriggerPopupData | undefined {
    const dialogType = this.rootStore.featureManagerStore.currentFeatureType;

    switch (dialogType) {
      case 'EventTriggerGifts':
        return this.getMaxWeightPopup('gift');
      case 'PersonalGift':
        return this.getMaxWeightPopup('personalGift');
      case 'EventTriggerMessages':
        return this.getMaxWeightPopup('message');
      case 'EventTriggerOffers':
        return this.getMaxWeightPopup('offer');
      case 'EventTriggerOffersV2':
        return this.getMaxWeightPopup('2-3-offer');
      case 'EventTriggerRollingOffers':
        return this.getMaxWeightPopup('rollingOffer');
      case 'EventTriggerRoad':
        return this.getMaxWeightPopup('road');
      default:
        return undefined;
    }
  }

  private getMaxWeightPopup(type: EventTriggerPopupType) {
    if (type === 'personalGift') {
      return this.activeEventsPopupsQueue.find((event) => event.benefit.benefitType === type);
    }

    const currentCoinType = this.rootStore.coinTypeStore.coinType.toLowerCase();

    const filteredPopups = this.activeEventsPopupsQueue.filter(
      (event) =>
        event.benefit.benefitType === type &&
        (!event.eventTrigger.extra?.coinMode || ['all', currentCoinType].includes(event.eventTrigger.extra.coinMode)),
    );

    const priorityPopups = filteredPopups.filter((popup) =>
      sessionStorage.getItem(`eventTriggerRollingOffers:${popup.userEventTriggerId}:purchaseCompleted`),
    );

    if (priorityPopups.length > 0) {
      return priorityPopups[0];
    }

    const restPopups = filteredPopups.filter((popup) => !priorityPopups.includes(popup));

    return restPopups.reduce(
      (acc, curr) => (acc.eventTrigger.weight > curr.eventTrigger.weight ? acc : curr),
      restPopups[0],
    );
  }

  private setAnimationProperties() {
    if (this.currentActiveEventPopup) {
      const { userEventTriggerId } = this.currentActiveEventPopup;
      const widget = document.getElementById(`${WIDGET_SAFE_ID_PREFIX}${userEventTriggerId}`);
      let rect = null;

      if (widget) {
        rect = widget?.getBoundingClientRect();
      } else {
        rect = document.body.getBoundingClientRect();
      }

      if (rect) {
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        document.documentElement.style.setProperty('--folding-duration', `${POPUP_ANIMATIOM_DURATION_SECS}s`);
        document.documentElement.style.setProperty('--widget-x', `${centerX}px`);
        document.documentElement.style.setProperty('--widget-y', `${centerY}px`);
      }
    }
  }

  public async setPopupState(args: { newIsOpen: boolean }) {
    if (this.isPopupCloseAnimationRunning) return;
    if (args.newIsOpen === this.isPopupOpen) return;

    this.setAnimationProperties();

    if (args.newIsOpen) {
      this.setPopupIsOpen(true);
      return;
    }

    this.setPopupCloseAnimationRunning(true);

    await sleep(POPUP_ANIMATIOM_DURATION_SECS * 1000);

    this.setPopupIsOpen(false);
    this.setPopupCloseAnimationRunning(false);
    this.setIsBusy(false);
  }

  public setEventToFirstInQueue(userEventTriggerId: string) {
    const activePopupEvent = this.activeEventsPopupsQueue.find(
      (event) => event.userEventTriggerId === userEventTriggerId,
    );
    if (!activePopupEvent) return;

    const filteredQueue = this.activeEventsPopupsQueue.filter(
      (event) => event.userEventTriggerId !== userEventTriggerId,
    );

    this.setActiveEventsPopupsQueue([activePopupEvent, ...filteredQueue]);
  }

  private onCloseListener: VoidFunction | undefined = undefined;
  private setOnCloseListener(onClose: VoidFunction | undefined) {
    this.onCloseListener = onClose;
  }

  public async openPopup(popup: EventTriggerPopupData, onClose?: VoidFunction, source?: PurchaseSource) {
    this.lastOfferSource = source || 'offer';
    this.setOnCloseListener(onClose);
    this.setCurrentActiveEvent(popup);
    this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenEventTriggerDialog });
    await this.setPopupState({ newIsOpen: true });
  }

  public async closePopup(preventCloseListener?: boolean) {
    if (this.onCloseListener && !preventCloseListener) {
      this.onCloseListener();
    }
    this.setOnCloseListener(undefined);
    await this.setPopupState({ newIsOpen: false });
    this.setCurrentActiveEvent(null);
    this.rootStore.uiStore.setCloseDialog();
  }

  private updateWidgetOnStartTimer(userEventTriggerId: string, expiredAt: Date) {
    const newWidgets = this.widgetEventsLists.map((widget) => {
      if (widget.userEventTriggerId === userEventTriggerId) {
        return {
          ...widget,
          expiredAt,
          canDisplay: true,
        };
      }
      return widget;
    });

    this.updateWidgetEvents(newWidgets);
  }

  private updateWidgetEvents(widgetsEvents: EventTriggerPopupData[]) {
    const lobbyWidgets = widgetsEvents.filter((event) => event.benefit.visibility?.lobby !== false);
    this.setWidgetEventsList(lobbyWidgets);

    const lobbyWidgetTimers = lobbyWidgets.reduce<Record<string, number>>((acc, event) => {
      const { rewardClaimEndDate, expiredAt } = event;
      let actualExpiredAt = expiredAt;

      if (isWithinRewardClaimWindow(expiredAt, rewardClaimEndDate) && !this.isRollingOfferWithPaidCurrentStep(event)) {
        actualExpiredAt = rewardClaimEndDate;
      }

      if (!event.userEventTriggerId) {
        return acc;
      }
      return {
        ...acc,
        [event.userEventTriggerId]: dayjs(actualExpiredAt).diff(dayjs(), 'second'),
      };
    }, {});

    const activePackagesTimers = lobbyWidgets.reduce((acc, event) => {
      if (!event.userEventTriggerId || !isRollingOfferPopupType(event.benefit)) {
        return acc;
      }

      const activeBenefitPackage = event.benefit.benefitPackages?.find((pkg) => pkg.isActive);

      if (!activeBenefitPackage) {
        return acc;
      }

      return {
        ...acc,
        [activeBenefitPackage.id]: activeBenefitPackage?.timeLeft || 0,
      };
    }, {});

    if (!isEmpty(lobbyWidgetTimers)) {
      const mergedTimers = {
        ...this.timers,
        ...lobbyWidgetTimers,
      };
      this.setTimers(mergedTimers);
      this.startTimers();
      this.setActiveBenefitPackagesTimers(activePackagesTimers);
      this.startActivePackagesTimers();
    }
  }

  public async claimGift() {
    let isClaimed = false;
    if (this.isActionBusy || !this.currentActiveEventPopup) return;
    this.setIsActionBusy(true);
    try {
      const { key: benefitKey } = this.currentActiveEventPopup.benefit;
      const activeEvent = {
        benefit: this.currentActiveEventPopup.benefit,
        eventTrigger: this.currentActiveEventPopup.eventTrigger,
        userEventTriggerId: this.currentActiveEventPopup.userEventTriggerId,
        isNewEventType: this.currentActiveEventPopup.isNewEventType,
      };
      await postRequest(`gift/${benefitKey}`, { activeEvent });
      isClaimed = true;
    } catch (e) {
      devError('Error while claiming gift', e);
    }
    this.setIsActionBusy(false);
    return isClaimed;
  }

  public async claimPersonalGift() {
    let isClaimed = false;
    if (this.isActionBusy || !this.currentActiveEventPopup) return;
    if (!isPersonalGiftPopupType(this.currentActiveEventPopup.benefit)) {
      devError('Trying to claim personal gift, but current active event is not personal gift');
      return;
    }
    this.setIsActionBusy(true);
    try {
      if (!this.currentActiveEventPopup?.userGift) {
        return;
      }
      const { id: userGiftId } = this.currentActiveEventPopup.userGift;
      await postRequest(`gift/personal-gift/${userGiftId}`);
      isClaimed = true;
    } catch (e) {
      devError('Error while claiming gift', e);
    }
    this.setIsActionBusy(false);
    return isClaimed;
  }

  public async notifyUserMessage(): Promise<boolean | undefined> {
    if (this.isActionBusy || !this.currentActiveEventPopup) return;
    this.setIsActionBusy(true);
    try {
      const { key: benefitKey } = this.currentActiveEventPopup.benefit;
      const activeEvent = {
        benefit: this.currentActiveEventPopup.benefit,
        eventTrigger: this.currentActiveEventPopup.eventTrigger,
        userEventTriggerId: this.currentActiveEventPopup.userEventTriggerId,
        isNewEventType: this.currentActiveEventPopup.isNewEventType,
      };

      const response = await postRequest<{ data: { isLastUsage: boolean } }>(`message/${benefitKey}`, { activeEvent });
      this.setIsActionBusy(false);
      return response.data.isLastUsage;
    } catch (e) {
      devError('Error while claiming gift', e);
    }
    this.setIsActionBusy(false);
  }

  public async enterRaffle(raffleId: string, event: EventTriggerPopupData): Promise<boolean> {
    const response = await postRequest<ApiResponse<EventTriggersResponse>>(`/v2/raffle/${raffleId}/enter`, {
      userEventTriggerId: event.userEventTriggerId,
      benefitKey: event.benefit.key,
    });

    const data = get(response, 'data.data', null);

    if (isEmpty(data)) {
      return false;
    }

    const dataWithEventsCompacted = this.compactEventTriggerData(data);

    const validatedEvents = reduceInvalidEventsData(dataWithEventsCompacted.currentEventsByTrigger);
    const validatedGifts = reduceInvalidPersonalGifts(dataWithEventsCompacted.personalGifts);

    const activeEventsData = this.handleActiveEventsPopups({
      ...dataWithEventsCompacted,
      currentEventsByTrigger: validatedEvents,
      personalGifts: validatedGifts,
    });
    this.setActiveEventPopups(activeEventsData, { removeRaffleIds: [raffleId] });

    const nextEvent =
      validatedEvents.find((e) => e.userEventTriggerId !== event.userEventTriggerId) ?? validatedGifts[0];

    if (nextEvent) {
      this.sendAnalyticsEvents({ event_action: 'impression', triggered_by: 'click' }, nextEvent);
      await this.openPopup(nextEvent);
      this.shownEventIds.add(nextEvent.userEventTriggerId);

      this.setActiveEventsPopupsQueue(
        this.activeEventsPopupsQueue.filter((e) => e.userEventTriggerId !== nextEvent.userEventTriggerId),
      );
      return true;
    }

    return false;
  }

  public async showNextPopup(): Promise<boolean> {
    if (this.isBusy) return false;
    this.setIsBusy(true);

    if (this.isPopupOpen) {
      await this.closePopup();
    }

    let nextPopup = this.getNextPopup();

    if (nextPopup) {
      if (this.isPopupOpen) {
        await sleep(300);
      }

      if (!nextPopup.canDisplay) {
        const popupId = nextPopup.isNewEventType ? nextPopup.userEventTriggerId : nextPopup.eventTrigger.eventTriggerId;
        const updatedPopup = await this.startEventTimer(popupId);

        if (updatedPopup) {
          nextPopup = updatedPopup;
        } else {
          // if timer is not started, something went wrong, so we should remove this popup from the queue
          return this.showNextPopup();
        }
      }

      const triggered_by = this.fetchCount === 1 ? 'login' : 'lobby';
      this.sendAnalyticsEvents(
        {
          event_action: 'impression',
          triggered_by,
        },
        nextPopup,
      );

      await this.openPopup(nextPopup);

      // Mark this event as shown to prevent re-triggering from socket events
      this.shownEventIds.add(nextPopup.userEventTriggerId);

      if (isPersonalGiftPopupType(nextPopup.benefit)) {
        this.setActiveEventsPopupsQueue(
          this.activeEventsPopupsQueue.filter((event) => event?.userGift?.id !== nextPopup?.userGift?.id),
        );
      } else {
        this.setActiveEventsPopupsQueue(
          this.activeEventsPopupsQueue.filter((event) => event.userEventTriggerId !== nextPopup?.userEventTriggerId),
        );
      }

      this.setIsBusy(false);
      return true;
    }
    this.setIsBusy(false);
    this.rootStore.featureManagerStore.onDialogFinished('EventTriggerGifts');
    return false;
  }

  private async preloadImages(images: string[]) {
    const promises = images.map((src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
      });
    });
    await Promise.all(promises);
  }

  private prepareEventsRiveFiles(events: EventTriggerPopupData[]) {
    const urls: string[] = [];
    for (const event of events) {
      const { riveLayout } = event.benefit;
      if ('riveFileUrl' in event.benefit && event.benefit.riveFileUrl) urls.push(event.benefit.riveFileUrl);
      if (riveLayout) urls.push(riveLayout);
      this.prepareActionEventRiveFiles(event, urls);
    }
    urls.forEach((url) => this.rootStore.riveFileStore.addFileToPreload(url));
  }

  private prepareActionEventRiveFiles(event: EventTriggerPopupData, urls: string[]) {
    if (event.benefit.benefitType === 'action') {
      const actionType = event?.eventTrigger?.extra?.actionType;
      if (actionType === EVENT_TRIGGER_ACTION_TYPE.COIN_TUTORIAL) {
        urls.push('/assets/rive/ccsc_toggle.riv');
      }
    }
  }

  private async prepareEventsImages(events: EventTriggerPopupData[]) {
    const images = events.reduce<string[]>((acc, event) => {
      if (typeof event.benefit.imageUrl === 'string') {
        acc.push(event.benefit.imageUrl);
      }
      return acc;
    }, []);

    if (!isEmpty(images)) {
      await this.preloadImages(images);
    }
  }

  private async onJoinedNewTournament() {
    await this.rootStore.featureManagerStore.fetchAndStartDialogs(['Tournament']);
  }

  private async prepareEventAssets(events: EventTriggerPopupData[]) {
    await Promise.allSettled([this.prepareEventsImages(events), this.prepareEventsRiveFiles(events)]);
  }

  private setActiveEventPopups(eventsData: EventTriggersResponse, options: { removeRaffleIds?: string[] } = {}) {
    try {
      this.setLastEventTriggerResponse(eventsData);

      const { currentEventsByTrigger, otherActiveEvents, personalGifts, joinedNewTournament } = eventsData;

      const validatedEventsByTrigger = reduceInvalidEventsData(currentEventsByTrigger);
      const validatedOtherActiveEvents = reduceInvalidEventsData(otherActiveEvents);
      const validatedPersonalGifts = reduceInvalidPersonalGifts(personalGifts);

      if (joinedNewTournament) {
        void this.onJoinedNewTournament();
      }

      void this.prepareEventAssets([
        ...validatedOtherActiveEvents,
        ...validatedEventsByTrigger,
        ...validatedPersonalGifts,
      ]);

      const removeRaffleSet = new Set(options.removeRaffleIds ?? []);
      const isRenderable = (event: EventTriggerPopupData) =>
        !isQuestEventTrigger(event.benefit) &&
        !isActionEventTrigger(event.benefit) &&
        !(removeRaffleSet.size > 0 && isRaffleBenefit(event.benefit) && removeRaffleSet.has(event.benefit.raffleId));

      const newActiveEventsQueue = uniqBy(
        [...validatedEventsByTrigger, ...validatedPersonalGifts, ...this.activeEventsPopupsQueue],
        'userEventTriggerId',
      );

      const filteredEvents = newActiveEventsQueue
        .filter(isRenderable)
        .filter((event) => !this.shownEventIds.has(event.userEventTriggerId));
      // Response entries come first so fresh state wins on userEventTriggerId collisions.
      const nextWidgetEvents = uniqBy(
        [...validatedOtherActiveEvents, ...validatedEventsByTrigger, ...this.widgetEventsLists],
        'userEventTriggerId',
      ).filter(isRenderable);
      const questEvent = newActiveEventsQueue.find((event) => isQuestEventTrigger(event.benefit));
      const actionEvents = validatedEventsByTrigger.filter((event) => isActionEventTrigger(event.benefit));

      if (questEvent && !this.rootStore.questStore.currentPlayerQuest) {
        this.rootStore.questStore.setCurrentPlayerQuest(questEvent.benefit);
      }

      this.setActiveEventsPopupsQueue(filteredEvents);
      this.updateWidgetEvents(nextWidgetEvents);
      this.updateStoreOffers(nextWidgetEvents);
      this.rootStore.actionEventTriggerStore.registerEvents(actionEvents);

      const currentPopupId = this.currentActiveEventPopup?.userEventTriggerId;
      if (currentPopupId) {
        const refreshed = nextWidgetEvents.find((e) => e.userEventTriggerId === currentPopupId);
        if (refreshed && refreshed !== this.currentActiveEventPopup) {
          this.setCurrentActiveEvent(refreshed);
        }
      }
    } catch (e) {
      const errorMessage = getErrorMessage(e);
      void logToNewRelic('setActiveEventPopups', errorMessage, 'error');
    }
  }

  public sendAnalyticsEvents(
    args: { event_action: EventAction; extraArgs?: any } & WidgetEventOptionalArgs,
    eventData?: EventTriggerPopupData,
  ) {
    try {
      const { event_action, action_result, button_text, click_location, triggered_by, event_value, extraArgs } = args;
      const { eventTrigger, benefit } = eventData || this.currentActiveEventPopup || {};
      const event_trigger_id = eventTrigger?.eventTriggerId;

      if (!benefit) return;

      let packageData = {};

      if (isOfferPopupType(benefit) || isPersonalGiftPopupType(benefit) || isGiftPopupType(benefit)) {
        packageData = {
          gc_coin: benefit.gcCoin,
          sc_coin: benefit.scCoin,
          package_id: benefit?.packageId,
        };
      } else if (isQuestEventTrigger(benefit)) {
        const milestonesCompleted = benefit.userQuest.userQuestMilestones.filter(
          (milestone) => !!milestone.completedAt,
        ).length;

        packageData = {
          milestones_completed: milestonesCompleted,
          num_of_milestones: benefit.userQuest.userQuestMilestones.length,
          package_id: benefit.rewardPackage?.packageId,
          quest_ordered: benefit.ordered,
        };
      } else if (isOfferV2PopupType(benefit)) {
        const riveAnimationEnable = resolveFlag('enable_offer_rive_animation', true);
        packageData = {
          is_rive: riveAnimationEnable && !!benefit.riveFileUrl,
        };
      }

      let feature_sub_type: EventFeatureSubType | null = null;
      let feature_order = 0;
      const { EventTriggerOffers, EventTriggerMessages, EventTriggerGifts, PersonalGift, EventTriggerRoad } =
        this.rootStore.featureManagerStore.dialogManagerData || {};
      switch (benefit.benefitType) {
        case 'offer': {
          feature_order = EventTriggerOffers?.order || 0;
          feature_sub_type = 'event_trigger_offers';
          break;
        }
        case 'gift': {
          feature_order = EventTriggerGifts?.order || 0;
          feature_sub_type = 'event_trigger_gifts';
          break;
        }
        case 'message': {
          feature_order = EventTriggerMessages?.order || 0;
          feature_sub_type = 'event_trigger_messages';
          break;
        }
        case 'personalGift': {
          feature_order = PersonalGift?.order || 0;
          feature_sub_type = 'personal_gift';
          break;
        }
        case 'road': {
          feature_order = EventTriggerRoad?.order || 0;
          feature_sub_type = 'event_trigger_crown_road';
          break;
        }
        default: {
          feature_sub_type = 'event_trigger_offers';
        }
      }

      const raffleData = isRaffleBenefit(benefit)
        ? {
            raffle_id: benefit.raffleId,
            ...(benefit.raffleStage
              ? {
                  event_category: 'raffle' as const,
                  event_name: RAFFLE_STAGE_TO_EVENT_NAME[benefit.raffleStage],
                }
              : {}),
          }
        : {};

      void analytics.sendEvent('widget', {
        event_action,
        feature_type: 'event_trigger',
        feature_sub_type,
        feature_order,
        triggered_by,
        action_result,
        button_text,
        click_location,
        event_value,
        ...extraArgs,
        event_trigger_id,
        benefit_type: benefit.benefitType,
        benefit_key: benefit.key,
        current_step: eventData?.currentStep || 1,
        ...packageData,
        ...raffleData,
      });
    } catch (e) {
      devError('EventTriggerStore.sendAnalyticsEvents', e);
    }
  }

  private subscribeToEventsReceivedSocket(queueData: { data: EventTriggersResponse }) {
    // Check for new events BEFORE updating the state
    const incomingEventIds = [
      ...queueData.data.currentEventsByTrigger,
      ...queueData.data.otherActiveEvents,
      ...queueData.data.personalGifts,
    ].map((event) => event.userEventTriggerId);

    const hasNewEvents = incomingEventIds.some((id) => !this.shownEventIds.has(id));

    this.setActiveEventPopups(queueData.data);

    // Only trigger dialog sequence if there are genuinely new events
    if (hasNewEvents) {
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(this.dialogTypes, true);
    }
  }

  private getDialogTypesByEventType(type: EventTriggerPopupType): FeatureManagerDialogType[] {
    switch (type) {
      case 'gift':
        return ['EventTriggerGifts'];
      case 'message':
        return ['EventTriggerMessages'];
      case 'offer':
      case '2-3-offer':
        return ['EventTriggerOffersV2'];
      case 'personalGift':
        return ['PersonalGift'];
      case 'rollingOffer':
        return ['EventTriggerRollingOffers'];
      case 'road':
        return ['EventTriggerRoad'];
      default:
        return ['EventTriggerGifts']; // default to EventTriggerGifts if type is not recognized (tournament / booster / etc)
    }
  }

  private subscribeToEventStartedSocket(eventStartedData: { eventTriggerId: string; type: EventTriggerPopupType }) {
    const { eventTriggerId, type } = eventStartedData;
    const event = [...this.widgetEventsLists, ...this.activeEventsPopupsQueue].find(
      (activeEvent) => activeEvent.eventTrigger.eventTriggerId === eventTriggerId,
    );

    if (event) {
      devLog(':subscribeToEventStartedSocket: event already in user events');
      return;
    }

    const eventInStoreOffers = this.storeOffers.find((storeOffer) => storeOffer.eventTriggerId === eventTriggerId);

    if (eventInStoreOffers) {
      devLog(':subscribeToEventStartedSocket: event already in store offers');
      return;
    }

    // 5 seconds to 5 minutes
    const timeoutMs = 5000 + Math.random() * (5 * 60 * 1000 - 5000);

    const dialogTypes = this.getDialogTypesByEventType(type);

    setTimeout(() => {
      void this.rootStore.featureManagerStore.fetchAndStartDialogs(dialogTypes);
    }, timeoutMs);
  }

  private async subscribeToClaimPackage() {
    await this.rootStore.featureManagerStore.fetchAndStartDialogs(this.dialogTypes);
  }
}
